import {
    BrowserUtils,
    ComponentElements,
    ComponentInjector,
    ComponentObject,
    ComponentSelectors,
    ComponentService,
    Inject,
    UIComponent
} from '@atypon/ui-utils';
import PopNotification from '../../../plugins/pop-notification/js/pop-notification';

declare global {
    interface Window { isShowRecommendedFetched: boolean; }
}

const events = {
    dataFetch: 'showRecommended:loaded'
};

class Texts extends ComponentObject {
}

class ClassList extends ComponentObject {
    base = 'show-recommended';
    delayLoad = 'delayLoad';
    delayedLoad = 'delayedLoad';
}

class Selectors extends ComponentSelectors {
    lazyLoad = '.lazy-load';
    delayLoad = '.delayLoad';
}

class Elements extends ComponentElements<Selectors> {
    protected _lazyload:HTMLElement[];
    protected  _delayLoad:HTMLElement[];
    wrapper: HTMLElement;

    get lazyload(): HTMLElement[] {
        return this.lazyload;
    }

    set lazyload(value:HTMLElement[])  {
         this._lazyload = value;
         value?.forEach(el => el.parentNode.removeChild(el));
    }

    get delayLoad(): HTMLElement[] {
        return this._delayLoad;
    }

    set delayLoad(value:HTMLElement[])  {
         this._delayLoad = value;
    }

    initialize(wrapper: HTMLElement): void {
        super.initialize(wrapper);
        this.lazyload = this.domUtils.getElements(this.selectors.lazyLoad, this.wrapper);
        this.delayLoad = this.domUtils.getElements(this.selectors.delayLoad, this.wrapper);
    }
}

interface ShowRecommended extends ComponentService<Selectors, Elements, ClassList, Texts> {
}

@ComponentInjector(Selectors, Elements, ClassList, Texts)
class ShowRecommended implements UIComponent {
    @Inject()
    protected browserUtils: BrowserUtils;

    protected desktopWidth: number = 768;
    protected isAccordion: boolean = true;

    get removeShowRecommendedIfNoData() {
        return true;
    }

    get ajaxUrl() {
        return this.wrapper.dataset.ajaxurl;
    }

    constructor(readonly wrapper: HTMLElement) {
    }

    initialize(): void {
        this.elements.initialize(this.wrapper);
        window.isShowRecommendedFetched = false;
        this.addEventListeners();
    }

    addEventListeners(): void {
        this.fetchContent().then(() => {
            this.afterAjax();
        });
    }

    async fetchContent() {
        let relatedTabs: HTMLElement[];
      
        // Check if the widget is inside an inactive #pane-pcw-related and screen width is >= desktopWidth
        const isInRelatedTab = this.wrapper.closest('#pane-pcw-related:not(.active)');
        const isDesktop = window.screen.width >= this.desktopWidth;

        if (isInRelatedTab && isDesktop) {
            // Find related tabs
            relatedTabs = this.domUtils.getElements('#pane-pcw-related, #pane-pcw-relatedcon');
            relatedTabs.forEach((tab) => {
                tab.addEventListener('click', () => {
                    this.getRecommendedData();
                });
            });
        } else {
            // Load content directly
            await this.getContent();
        }
    }

    async getContent() {
        try {
            const response = await fetch(this.ajaxUrl, { method: 'POST', body: 'onload=true' });
            if (response.status !== 200) {
                throw new Error('Failed to load content');
            }
            const content = await response.text();
            this.wrapper.innerHTML = content;
        } catch (e) {
            console.error('Error loading widget content:', e);
        }
    }

    async getRecommendedData() {
        if (!this.wrapper.classList.contains('js-reommended-loaded')) {
            await this.getContent();
            this.wrapper.classList.add('js-reommended-loaded');
        }
    }

    afterAjax() {
        const popupNotification = this.domUtils.getElements('.show-recommended.pop-notification');    
        if (popupNotification.length) {
            popupNotification.forEach(item => {
                new PopNotification(item).initialize();
            });
        } 
        // const showRecommended = this.domUtils.getElement('.show-recommended');
        this.truncate(this.wrapper);
        setTimeout(() => {
            if(this.isAccordion) {
                this.checkIfClosed();
            }
            this.hideSkeletons();
            this.emitFetchedEvent();
        }, 10)
    }

    protected truncate(widget: HTMLElement): void {
        if (!this.truncate) {
            return;
        }
        ($('.creative-work__title', widget) as any).not("[data-truncate='none']").truncate({
            lines: 3,
            addClass: 'min-height',
        });

        ($('.creative-work .loa', widget)as any).not("[data-truncate='none']").truncate({
            lines: 2,
            type: 'list',
            addClass: 'loa-height',
        });
    }

    emitFetchedEvent() {
        const fetchedEvent = new Event(events.dataFetch);
        this.wrapper.dispatchEvent(fetchedEvent);
        // Sometimes other JS components loads after this component therefore the event won't be listened to, so a global variable
        // would be helpful as well
        window.isShowRecommendedFetched = true;
    }

    checkIfClosed(): void {
        const accordionTab = this.wrapper.closest('.accordion-tabbed__tab');
        
        if (accordionTab && !accordionTab.classList.contains('js--open')) {
            const control = accordionTab.querySelector<HTMLElement>('.accordion-tabbed__control');
            const content = accordionTab.querySelector<HTMLElement>('.accordion-tabbed__content');
            
            if (control) {
                control.setAttribute('aria-expanded', 'false');
            }
            
            if (content) {
                content.style.display = 'none';
            }
        }
    }
    
    hideSkeletons() {
        this.elements.initialize(this.wrapper);
        this.elements.delayLoad.forEach(el => {
            el.classList.remove(this.classList.delayLoad);
            el.classList.add(this.classList.delayedLoad)
        });

        if (this.removeShowRecommendedIfNoData) {
            // Check that the response has actual data
            if (this.wrapper && this.wrapper.children[1] && this.wrapper.children[1].children[0]) {
                this.wrapper.replaceWith(...this.domUtils.convertToArray(this.wrapper.children));
            } else {
                this.wrapper.parentElement.classList.contains(this.classList.base) ? this.wrapper.parentElement.remove() : '';
            }
        } else {
            this.wrapper.replaceWith(...this.domUtils.convertToArray(this.wrapper.children));
        }
    }
}

export default ShowRecommended;

export {
    Elements as ShowRecommendedElements,
    Selectors as ShowRecommendedSelectors,
    ClassList as ShowRecommendedClassList,
    Texts as ShowRecommendedTexts,
}